/**
 * The single-NFT review-row contract — Phase 06.
 *
 * Every money path on the asset page builds its review through this one
 * function, so the disclosure set cannot drift between list, offer, accept and
 * the two cancellations: the required rows are produced here, in this order,
 * for all five. A flow supplies what it can source; anything it cannot source
 * becomes an explicit unavailable/pending value naming the dependency. No
 * branch in this file can produce a number that was not passed in.
 *
 * WHAT IS NOT SOURCEABLE TODAY, and why each row says so rather than showing a
 * figure (Q9 is unresolved — the repository was searched and no endpoint,
 * type or payload anywhere exposes any of these):
 *
 *   platform fee   No endpoint publishes one. The only fee figure that ever
 *                  existed in this codebase was a hardcoded 1% inside the
 *                  offer modal's summary block — a client-side literal with no
 *                  backend behind it. It is deleted, not relocated.
 *   royalty        `item.royalty` is a real percentage on the item payload and
 *                  is shown. Whether it is CHARGED on this transaction, and to
 *                  whom, is not stated anywhere in the API, so applicability is
 *                  carried as a note instead of being assumed.
 *   net proceeds   Depends on both of the above. Null in, null out — a net
 *                  figure computed from an assumed fee is the plausible-but-
 *                  wrong number on a seller's screen that D010 exists to stop.
 *   gas            `/gas-policy` returns network POLICY (bump percentages, a
 *                  max gas price, an estimate multiplier). It does not return
 *                  the fee for a transaction, and no other endpoint does.
 *   approval       No flow here requests a signature and no payload mentions
 *                  one; whether the backend performs one is not stated.
 */

import {
  type ReviewRow,
  type ReviewValue,
  pending,
  sourced,
  unavailable,
} from "@/components/ui/transaction-review-contract";
import { formatDecimalString } from "@/lib/token-amount";
import { netOf } from "@/lib/decimal-amount";
import { withPercent } from "@/lib/percent";

export type ReviewPerspective = "seller" | "buyer";

export type ReviewContext = {
  lang: "fa" | "en";
  perspective: ReviewPerspective;
  assetName: string;
  collectionName?: string | null;
  tokenId: string;
  /** Whole units. Null when the flow has no quantity yet. */
  quantity: number | null;
  /** Decimal string per item. Null until it exists. NEVER 0 for "unknown". */
  unitPrice: string | null;
  /** True when `unitPrice` was derived by division and had to be truncated. */
  unitPriceRounded?: boolean;
  /** Decimal string. Null until it exists. */
  total: string | null;
  currencySymbol: string | null;
  currencyUnavailableReason?: string;
  networkLabel: string;
  /** Already-formatted balance text plus its symbol, or null. */
  balance: { text: string; symbol: string } | null;
  /** Required when `balance` is null: why there is no balance to show. */
  balanceUnavailableReason: string;
  /** From `item.royalty`. Null when the payload carried none. */
  royaltyPercent: number | null;
  expiration: ReviewValue;
  conditions: ReviewValue;
};

function amount(text: string | null, symbol: string | null, locale: "fa" | "en"): string | null {
  if (text === null) return null;
  const formatted = formatDecimalString(text, { locale, maxFractionDigits: 18 }, "");
  if (!formatted) return null;
  return symbol ? `${formatted} ${symbol}` : formatted;
}

export function buildReviewRows(ctx: ReviewContext): ReviewRow[] {
  const isFa = ctx.lang === "fa";
  const locale = ctx.lang;

  const enterAmount = isFa
    ? "پس از وارد کردن مبلغ نمایش داده می‌شود."
    : "Shown once you enter an amount.";

  const unitPriceText = amount(ctx.unitPrice, ctx.currencySymbol, locale);
  const totalText = amount(ctx.total, ctx.currencySymbol, locale);

  // The platform fee and the royalty charge are BOTH unknown, so the net is
  // null. The call is written this way — rather than short-circuited — so that
  // the day either value becomes sourceable, passing it here makes the net row
  // compute and update live with no other change.
  const platformFeeAmount: string | null = null;
  const royaltyChargeAmount: string | null = null;
  const net = netOf(ctx.total, [platformFeeAmount, royaltyChargeAmount]);
  const netText = amount(net, ctx.currencySymbol, locale);

  const netLabel =
    ctx.perspective === "seller"
      ? isFa
        ? "خالص دریافتی شما"
        : "Your net proceeds"
      : isFa
        ? "هزینه‌ی نهایی شما"
        : "Your net cost";

  const rows: ReviewRow[] = [
    {
      id: "asset",
      label: isFa ? "دارایی" : "Asset",
      value: sourced(
        ctx.collectionName ? `${ctx.assetName} · ${ctx.collectionName}` : ctx.assetName,
      ),
    },
    {
      id: "token-id",
      label: isFa ? "شناسه توکن" : "Token ID",
      value: { kind: "value", text: `#${ctx.tokenId}`, technical: true },
    },
    {
      id: "quantity",
      label: isFa ? "تعداد" : "Quantity",
      value:
        ctx.quantity === null
          ? pending(enterAmount)
          : sourced(ctx.quantity.toLocaleString(isFa ? "fa-IR" : "en-US")),
    },
    {
      id: "unit-price",
      label: isFa ? "قیمت هر واحد" : "Unit price",
      value:
        unitPriceText === null
          ? pending(enterAmount)
          : sourced(
              unitPriceText,
              ctx.unitPriceRounded
                ? isFa
                  ? "محاسبه‌شده از مبلغ کل تقسیم بر تعداد و برای نمایش گرد شده است؛ مبلغ ارسالی همان مبلغ کل است."
                  : "Calculated from the total divided by the quantity and rounded for display. The amount submitted is the total."
                : undefined,
            ),
    },
    {
      id: "total",
      label: isFa ? "مبلغ کل" : "Total",
      emphasis: "total",
      value: totalText === null ? pending(enterAmount) : sourced(totalText),
    },
    {
      id: "currency",
      label: isFa ? "ارز" : "Currency",
      value: ctx.currencySymbol
        ? sourced(ctx.currencySymbol)
        : unavailable(
            ctx.currencyUnavailableReason ??
              (isFa
                ? "ارز این تراکنش هنوز مشخص نشده است."
                : "The currency for this transaction is not resolved yet."),
          ),
    },
    {
      id: "wallet-balance",
      label: isFa ? "موجودی کیف پول" : "Wallet balance",
      value: ctx.balance
        ? sourced(`${ctx.balance.text} ${ctx.balance.symbol}`)
        : unavailable(ctx.balanceUnavailableReason),
    },
    {
      id: "platform-fee",
      label: isFa ? "کارمزد پلتفرم" : "Platform fee",
      value: unavailable(
        isFa
          ? "هیچ سرویسی در API کارمزد پلتفرم را اعلام نمی‌کند. تا زمانی که بک‌اند آن را منتشر کند، این مقدار نمایش داده نمی‌شود و عدد جایگزینی ساخته نخواهد شد."
          : "No API endpoint publishes a platform fee. This row stays empty until the backend exposes one — no substitute figure is calculated.",
      ),
    },
    {
      id: "royalty",
      label: isFa ? "رویالتی" : "Royalty",
      value:
        ctx.royaltyPercent === null
          ? unavailable(
              isFa
                ? "اطلاعات این آیتم مقدار رویالتی ندارد."
                : "The item payload carries no royalty value.",
            )
          : sourced(
              withPercent(ctx.royaltyPercent.toLocaleString(isFa ? "fa-IR" : "en-US"), isFa),
              isFa
                ? "رویالتی ثبت‌شده‌ی این آیتم است. اینکه در این تراکنش دریافت می‌شود یا نه و به چه کسی می‌رسد، در API مشخص نشده است."
                : "This is the item's recorded royalty. Whether it is charged on this transaction, and to whom, is not stated by the API.",
            ),
    },
    {
      id: "net",
      label: netLabel,
      emphasis: "net",
      value:
        netText !== null
          ? sourced(netText)
          : ctx.total === null
            ? pending(enterAmount)
            : unavailable(
                isFa
                  ? "برای محاسبه، هم کارمزد پلتفرم و هم وضعیت دریافت رویالتی لازم است؛ هیچ‌کدام در API منتشر نشده‌اند. عدد تخمینی جایگزین نمایش داده نمی‌شود."
                  : "This needs both the platform fee and whether the royalty is charged. Neither is published by the API, and no estimate is substituted.",
              ),
    },
    {
      id: "network",
      label: isFa ? "شبکه" : "Network",
      value: sourced(ctx.networkLabel),
    },
    {
      id: "gas",
      label: isFa ? "کارمزد شبکه (گس)" : "Network fee (gas)",
      value: unavailable(
        isFa
          ? "سرویس gas-policy فقط سیاست شبکه را برمی‌گرداند، نه کارمزد این تراکنش را؛ سرویس دیگری هم این عدد را نمی‌دهد."
          : "The gas-policy endpoint returns network policy, not the fee for this transaction, and no other endpoint returns it.",
      ),
    },
    {
      id: "expiration",
      label: isFa ? "انقضا" : "Expiration",
      value: ctx.expiration,
    },
    {
      id: "conditions",
      label: isFa ? "شرایط" : "Conditions",
      value: ctx.conditions,
    },
    {
      id: "approval",
      label: isFa ? "تأیید یا امضای لازم" : "Required approval or signature",
      value: unavailable(
        isFa
          ? "این مسیر هیچ امضایی از کیف پول درخواست نمی‌کند و API هم اعلام نکرده که امضایی لازم است."
          : "This flow requests no wallet signature, and the API does not state whether one is required.",
      ),
    },
  ];

  return rows;
}
